import { useMemo } from "react";
import { TouchscreenProvider } from "@touchscreen/hooks";
import { KioskShell } from "./KioskShell.js";
import { HoldingScreen } from "./components/HoldingScreen.js";
import { readBootParams } from "./lib/bootParams.js";
import { readBootRetryEnv } from "./lib/bootRetryEnv.js";

const { retryBaseMs, retryMaxMs } = readBootRetryEnv();

/**
 * The URL alone must fully bootstrap this app. The venue reference is read once at cold boot
 * from `?venue=` or the legacy `/JUKE1234` path — everything else (session, venue context, brand)
 * follows from it. See touchscreen-frontend.md §Kiosk.
 */
export function App(): React.JSX.Element {
  const { venueRef, devKey } = useMemo(
    () => readBootParams(window.location.search, window.location.pathname),
    [],
  );

  if (!venueRef) {
    // Not a recoverable state — the URL itself is wrong. No retry loop can fix a missing venue
    // reference; this needs a site visit to correct the installed URL.
    return <HoldingScreen message="This screen isn't set up yet" />;
  }

  return (
    <TouchscreenProvider
      baseUrl={import.meta.env.VITE_API_BASE_URL}
      surface="kiosk"
      venueRef={venueRef}
      devKey={devKey ?? undefined}
      retryBaseMs={retryBaseMs}
      retryMaxMs={retryMaxMs}
    >
      <KioskShell />
    </TouchscreenProvider>
  );
}
